import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LanguageService } from '@aston/foundation';
import { Injectable, inject } from '@angular/core';
import { combineLatest, of } from 'rxjs';
import { Store } from '@ngrx/store';

import { SubsidiariesService } from '../../subsidiaries-module/services/subsidiaries.service';
import { ISubsidiaryBlockedForm, ISubsidiaryDetail } from '../../subsidiaries-module/models';
import { SubsidiaryStatus, LegalIdentifierStatus } from '../../subsidiaries-module/enums';
import { NewAccountService } from '../../account-module/services/new-account.service';
import * as SubsidiaryDetailStoreActions from '../subsidiary-detail-store/actions';
import { WebSocketService } from '../../shared-module/services/websocket.service';
import { checkAccountCreationIsForbidden } from '../../account-module/functions';
import { LoadSubsidiariesRequest } from '../subsidiaries-store/actions';
import { SubsidiariesStoreActions } from '../subsidiaries-store';
import * as RoutesDefinitions from '../../routes-definitions';
import * as AppStoreSelectors from '../app-store/selectors';
import * as AppStoreActions from '../app-store/actions';
import { WsEventType } from '../../shared-module/enums';
import { AppConstants } from '../../app.constants';

import * as featureSelectors from './selectors';
import * as featureActions from './actions';


@Injectable({providedIn: 'root'})
export class NewAccountStoreEffects {
	private actions$ = inject(Actions);
	private store = inject(Store);
	private subsidiariesService = inject(SubsidiariesService);
	private newAccountService = inject(NewAccountService);
	private ws = inject(WebSocketService);
	private languageService = inject(LanguageService);


	loadSubsidiaryFromLegalIdentifierRequestEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateSubsidiaryFromLegalIdentifierRequest),
		map(action => action.entity),
		map(subsidiary => subsidiary.legalIdentifierValidationStatus === LegalIdentifierStatus.Success ?
			// If legalIdentifier is valid so we can load and display info
			featureActions.UpdateSubsidiaryFromLegalIdentifierSuccess({entity: subsidiary}) :

			// If legalIdentifier is invalid, we have to warn the user and empty the fields
			AppStoreActions.OpenConfirmationModal({
				textsKey: `NewAccount.Company.LegalIdentifierInvalid.`,
				hideCancelBtn: true,
				confirmBtnText: 'Actions.Close'
			},
			featureActions.UpdateSubsidiaryFromLegalIdentifierSuccess({
				entity: {
					legalIdentifierValidationStatus: subsidiary.legalIdentifierValidationStatus,
					name: '',
					street: '',
					city: '',
					zipCode: '',
					country: '',
				}
			})
			)
		),
		catchError(error => of(featureActions.UpdateSubsidiaryFromLegalIdentifierFailure({error})))
	)
	);

	submitSubsidiaryRequestEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SubmitSubsidiaryFormRequest),
		// Allowed amount of accounting documents
		withLatestFrom(this.store.select(AppStoreSelectors.selectUser)),
		switchMap(([action, user]) => this.subsidiariesService.getAllSubsidiaries().pipe(map(subs => ({action, user, subs})))),
		filter(({action, user}) => !checkAccountCreationIsForbidden(user, action.entity)),
		switchMap(({action}) => this.subsidiariesService.createSubsidiary(action.entity).pipe(
			switchMap(entity => [
				featureActions.SubmitSubsidiaryFormSuccess({entity}),
				// fake subsidiary entity so we have the name in the topbar
				SubsidiaryDetailStoreActions.LoadSubsidiarySuccess({entity: entity as unknown as ISubsidiaryDetail}),
				AppStoreActions.StartScrollToTop(),
			]),
			catchError(error => error.specificError?.match(/TenantCountExceeded/) ?
				of(featureActions.ShowSubsidiaryBlocked({ entity: action.entity, blockedBy: 'InvalidSubsidiariesCount' })) :
				of(featureActions.SubmitSubsidiaryFormFailure({error})))
		))
	));

	cancelCreation$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CancelCreation),
		map(_ => AppStoreActions.Navigate({to: [RoutesDefinitions.getBasePath()]}))
	));

	submitSubsidiarySuccessEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SubmitSubsidiaryFormSuccess),
		switchMap(({entity}) => [
			featureActions.SubmitSubscriptionFormSuccess({ entity }),
			featureActions.SetCurrentSubsidiary({ subsidiaryId: entity.id }),
			SubsidiaryDetailStoreActions.LoadSubsidiaryRequest(entity.id),
			AppStoreActions.Navigate({to: [RoutesDefinitions.getNewAccountValidationWithIdFullPath(entity.id)]})
		])
	));

	submitSubsidiaryBlockedEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SubmitSubsidiaryFormRequest),
		withLatestFrom(this.store.select(AppStoreSelectors.selectUser)),
		switchMap(([action, user]) => this.subsidiariesService.getAllSubsidiaries().pipe(map(subs => ({action, user, subs})))),
		map(({action, user}) => [action, checkAccountCreationIsForbidden(user, action.entity)]),
		filter(([_, blockedReason]) => !!blockedReason),
		map(([action, blockedBy]: [any, string]) => featureActions.ShowSubsidiaryBlocked({ entity: action.entity, blockedBy })),
	));

	ShowSubsidiaryBlocked$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ShowSubsidiaryBlocked),
		map(action => {
			const subsidiaryBlockedForm: ISubsidiaryBlockedForm = {
				...action.entity,
				languageCode: this.languageService.getCurrentLanguage(),
				countryCode: action.entity.countryCode || null,
				contactMe: true
			};

			// Open warning modal
			return AppStoreActions.OpenConfirmationModal({
				textsKey: `NewAccount.Company.CreationBlocked.${action.blockedBy}.`,
				confirmBtnText: 'Actions.ContactMe',
				cancelBtnText: 'Actions.NoThanks'
			},

			// Contact Me
			featureActions.SubmitSubsidiaryBlockedFormRequest(subsidiaryBlockedForm),

			// Don't contact me
			featureActions.SubmitSubsidiaryBlockedFormRequest({
				...subsidiaryBlockedForm,
				contactMe: false
			})
			);
		})
	))

	submitSubsidiaryBlockedRequestEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SubmitSubsidiaryBlockedFormRequest),
		switchMap(action => this.subsidiariesService.createSubsidiaryFailure(action.entity).pipe(
			map(_ => featureActions.SubmitSubsidiaryBlockedFormSuccess({entity: action.entity})),
			catchError(error => of(featureActions.SubmitSubsidiaryBlockedFormFailure({error})))
		))
	));

	submitSubsidiaryBlockedSuccessEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SubmitSubsidiaryBlockedFormSuccess),
		map(_ => {
			window.location.href = AppConstants.CREATE_SUBSIDIARY_BLOCKED_REDIRECT_URL;
		})
	), {dispatch: false});

	submitSubscriptionSuccessEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SubmitSubscriptionFormSuccess),
		map(_ => AppStoreActions.Navigate({to: [RoutesDefinitions.getNewAccountValidationFullPath()]}))
	));

	onSubsidiaryStatusEventEffect$ = createEffect(() =>
		this.ws.ofType(WsEventType.RefreshSubscription).pipe(
			switchMap(_ => 
				combineLatest([
					this.store.select(featureSelectors.selectSelectedSubsidiary),
					this.subsidiariesService.getActiveSubsidiaries()
				]).pipe(
					switchMap(([selectedSubsidiary, subsidiaries]) => {
						const subsidiary = !!selectedSubsidiary && subsidiaries.find(s => s.subsidiaryId === selectedSubsidiary.subsidiaryId);
						if (subsidiary && subsidiary.status === SubsidiaryStatus.Initialized) {
							return [
								featureActions.SetSelectedSubsidiary({ subsidiary }),
								featureActions.SubsidiaryInitializedChanged(),
								LoadSubsidiariesRequest(),
							];
						}

						return [featureActions.SubsidiaryNotInitializedChanged(), LoadSubsidiariesRequest()];
					})
				)
			)
		));

	redirectToCreatedTenantEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.RedirectToCreatedTenant),
		withLatestFrom(this.store.select(featureSelectors.selectSelectedSubsidiary)),
		map(([action, subsidiary]) => SubsidiariesStoreActions.RedirectToTenant({
			subsidiaryId: subsidiary.subsidiaryId,
			path: action.path,
		}))
	));
}
